import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaPlus, FaSearch } from "react-icons/fa";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui-custom/Tabs";
import Button from "components/MaterialButton";
import Input from "components/MaterialInput";
import Card from "components/MaterialCard";
import QuotationSummary from "./QuotationSummary";
import PurchaseOrder from "./PurchaseOrder";
import RequestForQuoatation from "./RequestForQuoatation";
import Request from "./CreatePurchaseOrder";
import OCRtoPO from "./OCRtoPO";
import {
  ArrowDown,
  Settings2,
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  Camera,
  Bot,
  HomeIcon,
  GitPullRequestIcon,
  Clock,
  CheckCheck,
  X,
  XCircle,
  PlayCircle,
  FileText,
} from "lucide-react";
import { MdOutlineInventory2, MdOutlinePendingActions } from "react-icons/md";
import CreateWarehouseMaterial from "./CreateWarehouseMaterial";
import { BASE_URL } from "utils/constant";
import CreateMaterialRequest from "./CreateMaterialRequest";
import MaterialRequest from "./MaterialRequest";
import { set } from "date-fns";
import WarehouseMaterialSidebar from "./WarehouseMaterialSidebar";
import SendReceiveSideBar from "./SendReceiveSideBar";
import ProcurementHomeDashboard from "./ProcurementHomeDashboard";
import OrganizationEmptyState from "components/UI_components/OrganizationEmptyState";
import VendorEmptyIcon from "assets/empty state/VendorEmpty.svg";
import NoItemIcon from "assets/empty state/NoItems.svg";
import RequireAccess from "components/RequireAccess";
import Loader from "components/UI_components/Loader";
import LoadingScreen from "components/UI_components/LoadingScreen";
import { GoGitPullRequest } from "react-icons/go";
import { PiShoppingCartSimple } from "react-icons/pi";

const truncate = (str, max = 28) =>
  str && str.length > max ? str.slice(0, max - 1) + "…" : str || "—";

const getMaterialMeta = (material) => {
  if (!material) return "";
  const specification = material?.specification || material?.material_spec || "";
  const subcategory = material?.subcategory || "";
  const brand = material?.brand_name || material?.brand || "";
  return [specification, subcategory, brand].filter(Boolean).join(" | ");
};

const formatDate = (iso) => {
  if (!iso) return "N/A";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${d.getDate()} ${d.toLocaleString("en-GB", { month: "short" })} ${d.getFullYear()}`;
};

const Badge = ({ children }) => (
  <span className="inline-block text-[11px] font-medium text-purple-900 bg-purple-100 px-2 py-1 rounded-full max-w-full truncate">
    {children}
  </span>
);

const StatusPill = ({ label, className }) => (
  <span className={`inline-block text-[11px] font-medium px-2 py-1 rounded-full border whitespace-nowrap ${className}`}>
    {label}
  </span>
);

const Divider = () => (
  <div className="w-px h-8 bg-gray-200 mx-2 flex-shrink-0" />
);

const ViewBtn = ({ onClick }) => (
  <button
    onClick={onClick}
    className="px-3 py-1 text-xs border border-[#3B0069] rounded-md text-[#3B0069] hover:bg-[#F5EEF9] whitespace-nowrap flex-shrink-0"
  >
    View
  </button>
);

// ─── Status configs ───────────────────────────────────────────
const MR_STATUS = {
  DRAFT: { label: "In Progress", cls: "bg-gray-100 text-gray-700 border-gray-200" },
  OPEN: { label: "In Progress", cls: "bg-gray-100 text-gray-700 border-gray-200" },
  SENT: { label: "Sent", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  RETURN: { label: "Return", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  PARTIAL_DELIVERY: { label: "Partial Delivery", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  COMPLETED: { label: "Completed", cls: "bg-green-100 text-green-800 border-green-200" },
  REJECTED: { label: "Rejected", cls: "bg-red-100 text-red-800 border-red-200" },
  PENDING_APPROVAL: { label: "Pending Approval", cls: "bg-amber-100 text-amber-800 border-amber-200" },
};

const PO_STATUS = {
  open: { label: "Pending Approval", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Approved", cls: "bg-green-100 text-green-800 border-green-200" },
  closed: { label: "Cancelled", cls: "bg-red-100 text-red-800 border-red-200" },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-800 border-red-200" },
  canceled: { label: "Cancelled", cls: "bg-red-100 text-red-800 border-red-200" },
};

// ─── Shared row shell ─────────────────────────────────────────
const Row = ({ children }) => (
  <div className="flex items-center py-3 px-3 border border-gray-200 rounded-lg mb-2 bg-white gap-0">
    {children}
  </div>
);


const WAREHOUSE_TABS = ["Material", "Warehouse Purchases"];
const MATERIAL_REQUEST_TABS = [
  { label: "Pending Approval", value: ["PENDING_APPROVAL"], icon: Clock },
  { label: "In Progress", value: ["OPEN", "DRAFT", "SENT"], icon: PlayCircle },
  { label: "Partial Delivery", value: ["PARTIAL_DELIVERY"], icon: FileText },
  { label: "Completed", value: ["COMPLETED"], icon: CheckCheck },
  { label: "Rejected", value: ["REJECTED"], icon: XCircle },
];
const TABS = [
  { id: "Home", label: "Home", icon: HomeIcon },
  { id: "Material Request", label: "Material Request", icon: MdOutlinePendingActions },
  { id: "Request for Quotation", label: "Request for Quotation", icon: GoGitPullRequest },
  { id: "Purchase Order", label: "Purchase Order", icon: PiShoppingCartSimple },
];
const RFQ_STATUS_TABS = [
  { label: "In Progress", value: "OPEN", icon: PlayCircle },
  { label: "Ordered", value: "ORDERED", icon: CheckCheck },
  { label: "Closed", value: "CLOSED", icon: XCircle },
];
const PO_STATUS_TABS = [
  { label: "Pending", value: ["open"], icon: Clock },
  { label: "Approved", value: ["approved"], icon: CheckCheck },
  { label: "Completed", value: ["completed"], icon: Sparkles },
  {
    label: "Cancelled",
    value: ["cancelled", "canceled", "closed"],
    icon: XCircle,
  },
];
const MR_SORT_OPTIONS = [
  { label: "Sort by Recent", value: "created_on" },
  { label: "Sort by Delivery expected by", value: "expectedBy" },
];

const formatDisplayDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// const RequestRow = ({ request, onView }) => {
//   const getStatusConfig = (status) => {
//     const configs = {
//       DRAFT: {
//         label: "In Progress",
//         bgColor: "bg-gray-100",
//         textColor: "text-gray-700",
//         borderColor: "border-gray-200",
//       },
//       OPEN: {
//         label: "In Progress",
//         bgColor: "bg-gray-100",
//         textColor: "text-gray-700",
//         borderColor: "border-gray-200",
//       },
//       SENT: {
//         label: "Sent",
//         bgColor: "bg-blue-100",
//         textColor: "text-blue-700",
//         borderColor: "border-blue-200",
//       },
//       RETURN: {
//         label: "Return",
//         bgColor: "bg-orange-100",
//         textColor: "text-orange-700",
//         borderColor: "border-orange-200",
//       },
//       PARTIAL_DELIVERY: {
//         label: "Partial Delivery",
//         bgColor: "bg-yellow-100",
//         textColor: "text-yellow-700",
//         borderColor: "border-yellow-200",
//       },
//       COMPLETED: {
//         label: "Completed",
//         bgColor: "bg-green-100",
//         textColor: "text-green-700",
//         borderColor: "border-green-200",
//       },
//       REJECTED: {
//         label: "Rejected",
//         bgColor: "bg-red-100",
//         textColor: "text-red-700",
//         borderColor: "border-red-200",
//       },
//       PENDING_APPROVAL: {
//         label: "Pending Approval",
//         bgColor: "bg-yellow-100",
//         textColor: "text-yellow-700",
//         borderColor: "border-yellow-200",
//       },
//     };
//     return configs[status] || configs.SENT;
//   };

//   const statusConfig = getStatusConfig(request.status);

//   return (
//     <div className="flex items-center justify-between py-2 px-4 border border-gray-200 rounded-lg mb-3 bg-white">
//       <div className="w-[14%] font-medium text-[16px] text-[#232323]">
//         {request.code}
//       </div>

//       <div className="w-[14%] -ml-2">
//         <p className="text-[14px] text-gray-500">Content</p>
//         <p className="font-medium text-[16px] text-[#232323]">
//           {request.material_request_lines[0]?.material_name}{" "}
//           {request.material_request_lines.length > 1
//             ? `+ ${request.material_request_lines.length - 1} Items`
//             : ""}
//         </p>
//       </div>

//       <div className="w-[14%] flex justify-start -ml-7">
//         <div className="text-left">
//           <p className="text-[14px] text-gray-500">Raised by</p>
//           <p className="font-medium text-[16px] text-[#232323]">
//             {request.raisedBy}
//           </p>
//         </div>
//       </div>

//       <div className="w-[14%] ml-6">
//         <p className="text-[14px] text-gray-500">Delivery expected by</p>
//         <p className="font-medium text-[16px] text-[#232323]">
//           {formatDisplayDate(request.expectedBy)}
//         </p>
//       </div>

//       <div className="w-[16%]">
//         <div className="flex items-center text-center justify-center">
//           {!request.site_name ? (
//             <p className="text-xs font-medium text-purple-900 bg-purple-100 px-2 py-1 rounded-full">
//               Warehouse
//             </p>
//           ) : (
//             <p className="text-xs font-medium text-purple-900 bg-purple-100 px-2 py-1 rounded-full">
//               {request.site_name}
//             </p>
//           )}
//         </div>
//       </div>

//       <div className="w-[14%] flex justify-end pr-4 ml-4">
//         <span
//           className={`inline-block px-3 py-1 rounded-full text-[14px] font-medium border ${statusConfig.bgColor} ${statusConfig.textColor} ${statusConfig.borderColor}`}
//         >
//           {statusConfig.label}
//         </span>
//       </div>

//       <div className="w-[14%] flex justify-end pr-4">
//         <button
//           onClick={onView}
//           className="px-4 py-1 text-sm border-1 border-[#3B0069] rounded-md text-[#3B0069] hover:bg-[#F5EEF9]"
//         >
//           View
//         </button>
//       </div>
//     </div>
//   );
// };

// const RfqRow = ({ rfq, onView }) => {
//   return (
//     <div className="flex items-center justify-between py-2 px-4 border border-gray-200 rounded-lg mb-3 bg-white">
//       <div className="w-1/5 text-[16px] font-medium text-[#232323]">
//         {rfq.rfq_number}
//       </div>

//       <div className="w-1/5">
//         <p className="text-[16px] font-medium text-[#232323]">
//           {rfq.material_request_number}
//         </p>
//       </div>

//       <div className="w-1/5">
//         <p className="text-[14px] text-gray-500">Content</p>
//         <p className="text-[16px] font-medium text-[#232323]">
//           {rfq.lines[0]?.material_name}{" "}
//           {rfq.lines.length > 1 ? `+ ${rfq.lines.length - 1} Items` : ""}
//         </p>
//       </div>
//       <div className="w-1/5 text-center">
//         <div className="flex items-center justify-center space-x-2">
//           {rfq.is_warehouse_request ? (
//             <p className="text-xs font-medium text-purple-900 bg-purple-100 px-3 py-1 rounded-full">
//               Warehouse
//             </p>
//           ) : rfq.site_name ? (
//             <p className="text-xs font-medium text-purple-900 bg-purple-100 px-3 py-1 rounded-full">
//               {rfq.site_name || ""}
//             </p>
//           ) : null}
//         </div>
//       </div>

//       <div className="w-1/5 text-right">
//         <p className="text-[14px] text-gray-500">Raised on</p>
//         <p className="text-[16px] font-medium text-[#232323]">
//           {formatDisplayDate(rfq.created_on)}
//         </p>
//       </div>

//       <div className="w-1/5 pr-4 flex justify-end">
//         <button
//           onClick={() => onView(rfq)}
//           className="px-4 py-1 text-sm border-1 rounded-md text-[#3B0069] border-[#3B0069] hover:bg-[#F5EEF9]"
//         >
//           View
//         </button>
//       </div>
//     </div>
//   );
// };

// const PurchaseOrderRow = ({ po, onView }) => {
//   const formatPoDate = (iso) => {
//     if (!iso) return "N/A";
//     const d = new Date(iso);
//     if (isNaN(d)) return iso;
//     const day = d.getDate();
//     const month = d.toLocaleString("en-GB", { month: "short" });
//     const year = String(d.getFullYear());
//     return `${day} ${month} ${year}`;
//   };
//   const statusLower = (po.status || "").toLowerCase();
//   const statusLabel =
//     statusLower === "open"
//       ? "Pending Approval"
//       : statusLower === "closed" ||
//           statusLower === "cancelled" ||
//           statusLower === "canceled"
//         ? "Cancelled"
//         : po.status
//           ? po.status.charAt(0).toUpperCase() + po.status.slice(1)
//           : "Unknown";
//   return (
//     <div className="flex items-center justify-between py-2 px-4 border border-gray-200 rounded-lg mb-3 bg-white">
//       <div className="w-[14%] text-[16px] font-medium text-[#232323]">
//         {po.po_number}
//         <div className="text-[14px] font-normal text-gray-500">
//           {po.lines.length > 0 ? po.lines[0]?.material_name : ""}{" "}
//           {po.lines.length > 1 ? " + " + (po.lines.length - 1) + " more" : ""}
//         </div>
//       </div>

//       <div className="w-[14%]">
//         <p className="text-[16px] font-medium text-[#232323]">
//           {po.rfq_number}
//         </p>
//       </div>

//       <div className="w-[14%]">
//         <p className="text-[14px] text-gray-500">Vendor</p>
//         <p className="text-[16px] font-medium text-[#232323]">
//           {po.vendor_name || "N/A"}
//         </p>
//       </div>
//       <div className="w-[14%]">
//         <p className="text-[14px] text-gray-500">Created on</p>
//         <p className="text-[16px] font-medium text-[#232323]">
//           {formatPoDate(po.created_on)}
//         </p>
//       </div>
//       <div className="w-[16%] ">
//         <div className="flex items-center text-center justify-center">
//           {!po.site_name ? (
//             <p className="text-xs font-medium text-purple-900 bg-purple-100 px-2 py-1 rounded-full">
//               Warehouse
//             </p>
//           ) : po.site_name ? (
//             <p className="text-xs font-medium text-purple-900 bg-purple-100 px-2 py-1 rounded-full">
//               {po.site_name || ""}
//             </p>
//           ) : null}
//         </div>
//       </div>
//       <div className="w-[14%] flex justify-end pr-4">
//         <span
//           className={`text-[12px] px-2 py-1 ${
//             STATUS_STYLES[statusLower] || "text-gray-600 bg-gray-100"
//           }`}
//         >
//           {statusLabel}
//         </span>
//       </div>

//       <div className="w-[14%] flex justify-end pr-3">
//         <button
//           onClick={() => onView(po)}
//           className="px-4 py-1 text-sm border-1 rounded-md text-[#3B0069] border-[#3B0069] hover:bg-[#F5EEF9]"
//         >
//           View
//         </button>
//       </div>
//     </div>
//   );
// };

const WarehouseRow = ({ item, onView, onSendReceiveClick }) => {
  const lastStock = item.last_stock;
  const material = item.material;
  const materialMeta = getMaterialMeta(material);

  return (
    <div
      className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_220px] items-center gap-6 py-3 px-4 border border-gray-200 rounded-lg mb-3 bg-white"
      style={{
        transition: "transform 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.01)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      <div className="min-w-0 cursor-pointer" onClick={onView}>
        <div className="text-[16px] font-medium text-[#232323]">
          {material.material_name}
        </div>
        {materialMeta && <div className="text-[14px] text-gray-500">{materialMeta}</div>}
      </div>

      <div className="min-w-0 cursor-pointer" onClick={onView}>
        <div className="text-[14px] text-gray-500">In stock</div>
        <div className="text-[16px] font-medium text-[#232323]">
          {item.current_quantity} {material.uom}
        </div>
      </div>

      <div className="min-w-0 cursor-pointer" onClick={onView}>
        <div className="flex items-center gap-1 text-[#3B0069] text-sm">
          <span>{lastStock?.site_name || "Warehouse"}</span>
          {lastStock?.status === "DEDUCTED" &&
            lastStock?.status_reason === "SENT_TO_SITE" && (
              <span className="flex items-center">
                <ArrowLeft size={16} />
              </span>
            )}
          {lastStock?.status === "ADDED" && (
            <span className="flex items-center">
              <ArrowRight size={16} />
            </span>
          )}
          <span className="bg-[#ECE6F0] h-[28px] w-[28px] rounded-full flex justify-center items-center">
            <MdOutlineInventory2 size={24} className="p-1" />
          </span>
        </div>
        <div>
          {lastStock?.status === "DEDUCTED" &&
            lastStock?.deducted_quantity > 0 && (
              <div className="flex justify-start items-center gap-1 text-sm text-red-600">
                <ArrowDownRight size={16} /> {lastStock?.deducted_quantity}{" "}
                {material.uom}
              </div>
            )}
          {lastStock?.status === "ADDED" && lastStock?.entered_quantity > 0 && (
            <div className="flex justify-start items-center gap-1 text-sm text-green-600">
              <ArrowUpRight size={16} /> {lastStock?.entered_quantity}{" "}
              {material.uom}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onSendReceiveClick}
          className="px-3 py-1.5 text-sm border rounded-md text-[#3B0069] bg-[#ECE6F0] border-gray-200 hover:bg-gray-100"
        >
          Transfer Material
        </button>
      </div>
    </div>
  );
};

const STATUS_STYLES = {
  // open: "text-[#00C875] bg-[#FFFFFF] border-1 rounded-full border-[#ABEFC6]",
  closed: "text-[#D30606] bg-[#FFFFFF] border-1 rounded-full border-[#D30606]",
  cancelled:
    "text-[#D30606] bg-[#FFFFFF] border-1 rounded-full border-[#D30606]",
  canceled:
    "text-[#D30606] bg-[#FFFFFF] border-1 rounded-full border-[#D30606]",
  approved:
    "text-[#00C875] bg-[#FFFFFF] border-1 rounded-full border-[#ABEFC6]",
  completed:
    "text-[#0A66C2] bg-[#FFFFFF] border-1 rounded-full border-[#A3C9F5]",
  open: "text-[#F0B41D] bg-[#FFFFFF] border-1 rounded-full border-[#FEDF89]",
};




// ─── Material Request ─────────────────────────────────────────
const RequestRow = ({ request, onView }) => {
  const cfg = MR_STATUS[request.status] || MR_STATUS.SENT;
  const firstLineMaterial = request.material_request_lines[0] || null;
  const firstLine = firstLineMaterial?.material_name;
  const firstLineMeta = getMaterialMeta(firstLineMaterial);
  const extra = request.material_request_lines.length - 1;
  const siteName = request.site_name || "Warehouse";

  return (
    <Row>
      <div className="w-[17%] min-w-0 pr-2 flex-shrink-0">
        <Badge>{truncate(siteName, 22)}</Badge>
      </div>
      <Divider />
      <div className="w-[23%] min-w-0 pr-2">
        <p className="text-[13px] font-medium text-[#232323] truncate">{truncate(firstLine, 30)}</p>
        {firstLineMeta && <p className="text-[11px] text-gray-500 truncate">{truncate(firstLineMeta, 46)}</p>}
        {extra > 0 && <p className="text-[11px] text-gray-500">+ {extra} items</p>}
      </div>
      <Divider />
      <div className="w-[15%] min-w-0 pr-2 flex-shrink-0">
        <p className="text-[13px] font-medium text-[#232323] truncate">{request.code}</p>
      </div>
      <Divider />
      <div className="w-[21%] min-w-0 pr-2">
        <p className="text-[11px] text-gray-500">Raised by · Expected by</p>
        <p className="text-[13px] font-medium text-[#232323] truncate">
          {truncate(request.raisedBy, 12)} · {formatDate(request.expectedBy)}
        </p>
      </div>
      <Divider />
      <div className="w-[14%] flex-shrink-0">
        <StatusPill label={cfg.label} className={cfg.cls} />
      </div>
      <div className="w-[10%] flex justify-end flex-shrink-0">
        <ViewBtn onClick={onView} />
      </div>
    </Row>
  );
};

// ─── RFQ ─────────────────────────────────────────────────────
const RfqRow = ({ rfq, onView }) => {
  const firstLineMaterial = rfq.lines[0] || null;
  const firstLine = firstLineMaterial?.material_name;
  const firstLineMeta = getMaterialMeta(firstLineMaterial);
  const extra = rfq.lines.length - 1;
  const siteName = rfq.is_warehouse_request || !rfq.site_name ? "Warehouse" : rfq.site_name;

  return (
    <Row>
      <div className="w-[17%] min-w-0 pr-2 flex-shrink-0">
        <Badge>{truncate(siteName, 22)}</Badge>
      </div>
      <Divider />
      <div className="w-[23%] min-w-0 pr-2">
        <p className="text-[13px] font-medium text-[#232323] truncate">{truncate(firstLine, 30)}</p>
        {firstLineMeta && <p className="text-[11px] text-gray-500 truncate">{truncate(firstLineMeta, 46)}</p>}
        {extra > 0 && <p className="text-[11px] text-gray-500">+ {extra} items</p>}
      </div>
      <Divider />
      <div className="w-[15%] min-w-0 pr-2 flex-shrink-0">
        <p className="text-[13px] font-medium text-[#232323] truncate">{rfq.rfq_number}</p>
        <p className="text-[11px] text-gray-500 truncate">{rfq.material_request_number}</p>
      </div>
      <Divider />
      <div className="w-[21%] min-w-0 pr-2">
        <p className="text-[11px] text-gray-500">Raised on</p>
        <p className="text-[13px] font-medium text-[#232323]">{formatDate(rfq.created_on)}</p>
      </div>
      <Divider />
      <div className="w-[14%] flex-shrink-0">
        {/* RFQ has no status — reserve space or add if available */}
      </div>
      <div className="w-[10%] flex justify-end flex-shrink-0">
        <ViewBtn onClick={() => onView(rfq)} />
      </div>
    </Row>
  );
};

// ─── Purchase Order ───────────────────────────────────────────
const PurchaseOrderRow = ({ po, onView }) => {
  const statusKey = (po.status || "").toLowerCase();
  const cfg = PO_STATUS[statusKey] || { label: po.status || "Unknown", cls: "bg-gray-100 text-gray-700 border-gray-200" };
  const firstLineMaterial = po.lines[0] || null;
  const firstLine = firstLineMaterial?.material_name;
  const firstLineMeta = getMaterialMeta(firstLineMaterial);
  const extra = po.lines.length - 1;
  const siteName = po.site_name || "Warehouse";

  return (
    <Row>
      <div className="w-[17%] min-w-0 pr-2 flex-shrink-0">
        <Badge>{truncate(siteName, 22)}</Badge>
      </div>
      <Divider />
      <div className="w-[23%] min-w-0 pr-2">
        <p className="text-[13px] font-medium text-[#232323] truncate">{truncate(firstLine, 30)}</p>
        {firstLineMeta && <p className="text-[11px] text-gray-500 truncate">{truncate(firstLineMeta, 46)}</p>}
        {extra > 0 && <p className="text-[11px] text-gray-500">+ {extra} items</p>}
      </div>
      <Divider />
      <div className="w-[15%] min-w-0 pr-2 flex-shrink-0">
        <p className="text-[13px] font-medium text-[#232323] truncate">{po.po_number}</p>
        <p className="text-[11px] text-gray-500 truncate">{po.rfq_number}</p>
      </div>
      <Divider />
      <div className="w-[21%] min-w-0 pr-2">
        <p className="text-[11px] text-gray-500">Vendor · Order date</p>
        <p className="text-[13px] font-medium text-[#232323] truncate">
          {truncate(po.vendor_name || "N/A", 14)} · {formatDate(po.order_date)}
        </p>
      </div>
      <Divider />
      <div className="w-[14%] flex-shrink-0">
        <StatusPill label={cfg.label} className={cfg.cls} />
      </div>
      <div className="w-[10%] flex justify-end flex-shrink-0">
        <ViewBtn onClick={() => onView(po)} />
      </div>
    </Row>
  );
};

export default function Procurement({ currentMember, permissionsLoaded }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Home");
  const [search, setSearch] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [activeSection, setActiveSection] = useState("Request & Order");
  const [materials, setMaterials] = useState(
    JSON.parse(localStorage.getItem("materials")) || [],
  );
  const [rfqs, setRfqs] = useState(
    JSON.parse(localStorage.getItem("rfqs")) || [],
  );
  const [purchaseOrders, setPurchaseOrders] = useState(
    JSON.parse(localStorage.getItem("purchaseOrders")) || [],
  );
  const [activeRfqStatus, setActiveRfqStatus] = useState("OPEN");
  const [activePoStatus, setActivePoStatus] = useState(PO_STATUS_TABS[0].value);
  const [showCreateWarehouseMaterial, setShowCreateWarehouseMaterial] =
    useState(false);
  const [organisationId, setOrganisationId] = useState(
    JSON.parse(localStorage.getItem("organization"))?.id,
  );
  const [requests, setRequests] = useState(
    JSON.parse(localStorage.getItem("requests")) || [],
  );
  const [activeRequestStatus, setActiveRequestStatus] = useState(
    MATERIAL_REQUEST_TABS[0].value,
  );
  const [WarehouseLoading, setWarehouseLoading] = useState(false);
  const [RequestLoading, setRequestLoading] = useState(false);
  const [RfqLoading, setRfqLoading] = useState(false);
  const [PoLoading, setPoLoading] = useState(false);
  const [LibraryLoading, setLibraryLoading] = useState(false);
  const [openOCRModal, setOpenOCRModal] = useState(false);
  const [sortRecentDesc, setSortRecentDesc] = useState(true);
  const [mrSortField, setMrSortField] = useState("created_on");

  const pageLoading =
    WarehouseLoading ||
    RequestLoading ||
    RfqLoading ||
    PoLoading ||
    LibraryLoading;

  const sortedWarehouseMaterials = useMemo(() => {
    if (!materials || materials.length === 0) return materials;
    return [...materials].sort((a, b) =>
      sortRecentDesc
        ? new Date(b.created_on) - new Date(a.created_on)
        : new Date(a.created_on) - new Date(b.created_on),
    );
  }, [materials, sortRecentDesc]);

  const getMaterials = async () => {
    if (!materials.length) {
      setWarehouseLoading(true);
    }
    try {
      const res = await fetch(`${BASE_URL}/org/warehouse/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ org_id: organisationId }),
      });

      if (!res.ok) {
        throw new Error("Failed to fetch materials");
      }

      const data = await res.json();
      console.log("Fetched Materials:", data);

      setMaterials(data);
      localStorage.setItem("materials", JSON.stringify(data));
    } catch (err) {
      // setError("Failed to load materials from library");
      console.error("Error fetching materials:", err);
    } finally {
      setWarehouseLoading(false);
    }
  };

  const getMaterialRequests = async () => {
    if (!requests.length) {
      setRequestLoading(true);
    }
    try {
      const res = await fetch(
        `${BASE_URL}/inventory/material-requests/org/${organisationId}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );

      if (!res.ok) {
        throw new Error("Failed to fetch material requests");
      }

      const data = await res.json();
      // console.log("Fetched Material Requests:", data);
      const mrs = [];
      data.forEach((mr) =>
        mrs.push({
          ...mr,
          code: mr.mr_number,
          items: mr.material_request_lines.length,
          raisedBy: mr.created_by,
          expectedBy: mr.due_date,
        }),
      );
      setRequests(mrs);
      localStorage.setItem("requests", JSON.stringify(mrs));
    } catch (err) {
      // setError("Failed to load material requests");
      console.error("Error fetching material requests:", err);
    } finally {
      setRequestLoading(false);
    }
  };

  const getRfqs = async () => {
    if (!rfqs.length) {
      setRfqLoading(true);
    }
    try {
      const res = await fetch(
        `${BASE_URL}/inventory/rfq/org/${organisationId}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );
      if (!res.ok) {
        throw new Error("Failed to fetch RFQs");
      }
      const data = await res.json();
      setRfqs(data);
      localStorage.setItem("rfqs", JSON.stringify(data));
    } catch (err) {
      console.error("Error fetching RFQs:", err);
      // setError("Failed to load RFQs");
    } finally {
      setRfqLoading(false);
    }
  };

  const getPos = async () => {
    if (!purchaseOrders.length) {
      setPoLoading(true);
    }
    try {
      const res = await fetch(
        `${BASE_URL}/inventory/purchase-orders/org/${organisationId}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );
      if (!res.ok) {
        throw new Error("Failed to fetch Purchase Orders");
      }
      const data = await res.json();
      // console.log("Fetched Purchase Orders:", data);
      setPurchaseOrders(data.reverse());
      localStorage.setItem("purchaseOrders", JSON.stringify(data));
    } catch (err) {
      console.error("Error fetching Purchase Orders:", err);
      // setError("Failed to load Purchase Orders");
    } finally {
      setPoLoading(false);
    }
  };

  useEffect(() => {
    getMaterials();
    getMaterialRequests();
    getRfqs();
    getPos();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = (params.get("section") || "").toLowerCase();
    const tab = (params.get("tab") || "").toLowerCase();
    const mrId = params.get("mrId");
    const rfqId = params.get("rfqId");
    const poId = params.get("poId");

    if (section === "request-order") {
      setActiveSection("Request & Order");
    }

    if (tab === "home") {
      setActiveTab("Home");
    } else if (tab === "mr" || tab === "material-request") {
      setActiveTab("Material Request");
    } else if (tab === "rfq") {
      setActiveTab("Request for Quotation");
    } else if (tab === "po") {
      setActiveTab("Purchase Order");
    }

    if (mrId) {
      const targetRequest = requests.find(
        (item) => String(item.id) === String(mrId),
      );
      if (targetRequest) {
        setActiveSection("Request & Order");
        setActiveTab("Material Request");
        setOpenRequest(targetRequest);
        navigate("/dashboard/procurement", { replace: true });
      }
      return;
    }

    if (rfqId) {
      const targetRfq = rfqs.find((item) => String(item.id) === String(rfqId));
      if (targetRfq) {
        setActiveSection("Request & Order");
        setActiveTab("Request for Quotation");
        setOpenRfq(targetRfq);
        navigate("/dashboard/procurement", { replace: true });
      }
      return;
    }

    if (poId) {
      const targetPo = purchaseOrders.find((item) => String(item.id) === String(poId));
      if (targetPo) {
        setActiveSection("Request & Order");
        setActiveTab("Purchase Order");
        setOpenPo(targetPo);
        navigate("/dashboard/procurement", { replace: true });
      }
    }
  }, [location.search, navigate, requests, rfqs, purchaseOrders]);

  // Ensure default sub-tabs are selected whenever the main tab changes
  useEffect(() => {
    if (activeTab === "Material Request") {
      // Default to "Pending Approval"
      setActiveRequestStatus(MATERIAL_REQUEST_TABS[0].value);
      setMrSortField("created_on");
    } else if (activeTab === "Request for Quotation") {
      // Default to "In Progress"
      setActiveRfqStatus("OPEN");
    } else if (activeTab === "Purchase Order") {
      // Default to "Pending"
      setActivePoStatus(PO_STATUS_TABS[0].value);
    }
  }, [activeTab]);

  const filtered = useMemo(() => {
    if (
      activeSection !== "Request & Order" ||
      activeTab !== "Material Request"
    ) {
      return requests;
    }

    const base = (
      !search
        ? requests
        : requests.filter((r) =>
          [
            r.code,
            String(r.items),
            r.raisedBy,
            r.site_name || "warehouse",
            formatDisplayDate(r.expectedBy),
          ]
            .join(" ")
            .toLowerCase()
            .includes(search.toLowerCase()),
        )
    ).filter((r) => activeRequestStatus.includes(r.status));

    return [...base].sort((a, b) => {
      const aSortDate =
        mrSortField === "created_on" ? a.created_on : a.expectedBy;
      const bSortDate =
        mrSortField === "created_on" ? b.created_on : b.expectedBy;

      // MR recent must always keep newest on top
      return new Date(bSortDate) - new Date(aSortDate);
    });
  }, [
    requests,
    search,
    activeSection,
    activeTab,
    activeRequestStatus,
    sortRecentDesc,
    mrSortField,
  ]);

  const filteredRfqs = useMemo(() => {
    if (activeTab !== "Request for Quotation") {
      return rfqs;
    }
    const base = rfqs.filter((rfq) => rfq.status === activeRfqStatus);
    return [...base].sort((a, b) =>
      sortRecentDesc
        ? new Date(b.created_on) - new Date(a.created_on)
        : new Date(a.created_on) - new Date(b.created_on),
    );
  }, [rfqs, activeRfqStatus, activeTab, sortRecentDesc]);

  const filteredPos = useMemo(() => {
    if (activeTab !== "Purchase Order") {
      return purchaseOrders;
    }
    const base = purchaseOrders.filter((po) =>
      activePoStatus.includes((po.status || "").toLowerCase()),
    );
    return [...base].sort((a, b) =>
      sortRecentDesc
        ? new Date(b.created_on) - new Date(a.created_on)
        : new Date(a.created_on) - new Date(b.created_on),
    );
  }, [purchaseOrders, activeTab, sortRecentDesc, activePoStatus]);

  const warehousePos = useMemo(() => {
    const base = purchaseOrders.filter((po) => !po.site_name);
    return [...base].sort((a, b) =>
      sortRecentDesc
        ? new Date(b.created_on) - new Date(a.created_on)
        : new Date(a.created_on) - new Date(b.created_on),
    );
  }, [purchaseOrders, sortRecentDesc]);

  const [openRfq, setOpenRfq] = useState(null);
  const [openPo, setOpenPo] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateMR, setShowCreateMR] = useState(false);
  const [showCreateRFQ, setShowCreateRFQ] = useState(false);
  const [showCreatePO, setShowCreatePO] = useState(false);
  const [openRequest, setOpenRequest] = useState(null);
  const [showSendReceiveSideBar, setShowSendReceiveSideBar] = useState(false);
  const [showOCRModal, setShowOCRModal] = useState(false);
  const inProgressRequestStatus =
    MATERIAL_REQUEST_TABS.find((tab) => tab.label === "In Progress")?.value ||
    MATERIAL_REQUEST_TABS[1].value;
  const isCreateRequestSidebarOpen =
    showCreateMR || showCreateRFQ || showCreatePO;
  const showInlineCreatePanel =
    showCreateModal &&
    activeSection === "Request & Order";
  const createPanelRef = useRef(null);
  const requestSidebarRef = useRef(null);

  useEffect(() => {
    if (!showInlineCreatePanel) return;

    const handleOutsideClick = (event) => {
      if (
        createPanelRef.current &&
        !createPanelRef.current.contains(event.target)
      ) {
        setShowCreateModal(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showInlineCreatePanel]);

  useEffect(() => {
    if (!isCreateRequestSidebarOpen) return;

    const handleOutsideSidebarClick = (event) => {
      const target = event.target;
      if (
        target &&
        typeof target.closest === "function" &&
        target.closest(".create-request-panel")
      ) {
        return;
      }
      if (
        createPanelRef.current &&
        createPanelRef.current.contains(event.target)
      ) {
        return;
      }
      if (
        requestSidebarRef.current &&
        !requestSidebarRef.current.contains(event.target)
      ) {
        setShowCreateMR(false);
        setShowCreateRFQ(false);
        setShowCreatePO(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideSidebarClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideSidebarClick);
    };
  }, [isCreateRequestSidebarOpen]);

  const openCreateSidebar = (type) => {
    setShowCreateMR(type === "mr");
    setShowCreateRFQ(type === "rfq");
    setShowCreatePO(type === "po");
  };

  const handleDashboardAlertView = (alert) => {
    const params = new URLSearchParams({
      section: "request-order",
    });

    if (alert?.type === "mr") {
      params.set("tab", "material-request");
      params.set("mrId", String(alert.id));
    } else if (alert?.type === "rfq") {
      params.set("tab", "rfq");
      params.set("rfqId", String(alert.id));
    } else if (alert?.type === "po") {
      params.set("tab", "po");
      params.set("poId", String(alert.id));
    } else {
      return;
    }

    navigate(`/dashboard/procurement?${params.toString()}`);
  };

  if (!organisationId) {
    return <OrganizationEmptyState />;
  }

  if (
    currentMember?.procurement_control === false &&
    currentMember?.joined_as !== "owner" &&
    currentMember?.is_admin === false &&
    permissionsLoaded
  ) {
    return (
      <RequireAccess
        module="Procurement"
        currentMember={currentMember}
        permission="procurement_control"
      />
    );
  }

  return (
    <div
      className={`px-10 pt-8 pb-1 flex flex-col relative overflow-x-auto transition-[padding] duration-300 ease-in-out ${
        isCreateRequestSidebarOpen ? "lg:pr-[34rem]" : ""
      }`}
      style={{ overflowY: "auto" }}
    >
      <LoadingScreen loading={pageLoading}>
        {activeSection === "Warehouse" ? (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {WAREHOUSE_TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 rounded-full text-[16px] ${activeTab === tab
                    ? "bg-[#ECE6F0] text-[#3B0069] border-2 border-[#3B0069]"
                    : "bg-[#E3E3E3] text-[#737373] border-gray-200 hover:bg-gray-200"
                    }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="text-sm ml-4 text-gray-600">
                {activeTab === "Material"
                  ? `${materials.length} Materials`
                  : `${warehousePos.length} Deliveries`}
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="flex items-center gap-1 text-sm text-[#3B0069] border border-gray-200 rounded-full px-3 py-1 bg-[#ECE6F0] hover:bg-[#e0d4e9] transition"
                  onClick={() => setSortRecentDesc((p) => !p)}
                >
                  Sorted by Recent
                  <span>
                    <ArrowDown
                      size={14}
                      className={`${sortRecentDesc ? "" : "rotate-180"} transition-transform`}
                    />
                  </span>
                </button>

                {/* <div className="w-[28px] h-[28px] rounded-full flex items-center justify-center bg-[#ECE6F0]">
                <Settings2 size={14} />
              </div> */}
                <Button
                  onClick={() => setShowCreateWarehouseMaterial(true)}
                  className="flex items-center gap-2 bg-[#3B0069] h-[36px] text-white px-4 py-2 rounded-lg"
                >
                  <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                    <FaPlus size={12} />
                  </span>
                  <span>Material</span>
                </Button>
              </div>
            </div>

            <Card>
              <div className="">
                {activeTab !== "Material" ? (
                  <div className="h-[60vh] px-2 overflow-y-auto">
                    {PoLoading ? (
                      <div className="flex items-center justify-center py-10">
                        <Loader />
                      </div>
                    ) : warehousePos.length > 0 ? (
                      warehousePos.map((po, idx) => (
                        <PurchaseOrderRow
                          key={`${po.id}_${idx}`}
                          po={po}
                          onView={(p) => setOpenPo(p)}
                        />
                      ))
                    ) : (
                      <div className="flex flex-col items-center justify-center py-10">
                        <img
                          src={NoItemIcon}
                          draggable={false}
                          alt="No Items"
                          className="mb-4"
                          width={220}
                        />
                        <span className="text-gray-500 text-2xl">
                          No Purchase Orders
                        </span>
                        <span className="text-gray-500 text-sm">
                          No purchase orders found for warehouse
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-[60vh] px-2 overflow-y-auto">
                    {materials.length > 0 ? (
                      sortedWarehouseMaterials.map((m, idx) => (
                        <WarehouseRow
                          key={`${idx}`}
                          item={m}
                          onView={() => setSelectedMaterial(m)}
                          onSendReceiveClick={() =>
                            setShowSendReceiveSideBar(m)
                          }
                        />
                      ))
                    ) : (
                      <div className="flex flex-col items-center justify-center py-10">
                        <img
                          src={VendorEmptyIcon}
                          draggable={false}
                          alt="No Vendors"
                          className="mb-4"
                          width={240}
                        />
                        <span className="text-gray-500 text-2xl">
                          No Materials in Warehouse
                        </span>
                        <span className="text-gray-500 text-sm">
                          Add a material to the warehouse now
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </>
        ) : activeSection === "Request & Order" ? (
          <>
            {activeTab === "Home" && (
              <div className="mb-4">
                <h2 className="text-2xl leading-tight font-semibold text-[#232323]">
                  Procurement Dashboard
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Track materials, orders, and deliveries across all projects
                </p>
              </div>
            )}
            <div className="mb-4 flex items-start justify-between gap-3">
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full min-w-0"
              >
                <TabsList className="min-h-9 h-auto p-1 !justify-start flex-wrap items-start gap-y-1">
                  {TABS.map((tab) => {
                    let selfCount = 0;
                    if (tab.id === "Material Request") {
                      selfCount = requests.length;
                    } else if (tab.id === "Request for Quotation") {
                      selfCount = rfqs.length;
                    } else if (tab.id === "Purchase Order") {
                      selfCount = purchaseOrders.length;
                    }
                    const Icon = tab.icon;
                    return (
                      <TabsTrigger
                        key={tab.id}
                        value={tab.label}
                        className="h-7 text-sm px-2 gap-1.5 relative"
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {tab.label} ({selfCount})
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </Tabs>
              <div className="relative shrink-0" ref={createPanelRef}>
                <Button
                  className="flex items-center gap-2 bg-[#3B0069] text-white px-4 py-2 rounded-lg"
                  onClick={() => setShowCreateModal((prev) => !prev)}
                >
                  <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                    <FaPlus size={12} />
                  </span>
                  <span>Create Request</span>
                </Button>

                {showInlineCreatePanel && (
                  <div className="create-request-panel absolute right-0 top-full mt-2 w-[320px] bg-white border border-gray-200 rounded-xl shadow-xl z-30 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-[#232323]">
                        Create Request
                      </h3>
                      <button
                        onClick={() => setShowCreateModal(false)}
                        className="w-7 h-7 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
                        aria-label="Close create request options"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <button
                      onClick={() => {
                        openCreateSidebar("mr");
                      }}
                      className="w-full flex items-center justify-between px-3 py-3 rounded-lg bg-[#F5EEF9] text-[#3B0069] mb-2 border border-[#E7D9F0] hover:bg-[#EFE4F5]"
                    >
                      <span className="text-sm font-medium">Material Request</span>
                      <span className="w-6 h-6 rounded-full bg-[#3B0069] flex items-center justify-center">
                        <FaPlus size={12} className="text-white" />
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        openCreateSidebar("rfq");
                      }}
                      className="w-full flex items-center justify-between px-3 py-3 rounded-lg bg-[#F5EEF9] text-[#3B0069] mb-2 border border-[#E7D9F0] hover:bg-[#EFE4F5]"
                    >
                      <span className="text-sm font-medium">Raise RFQ</span>
                      <span className="w-6 h-6 rounded-full bg-[#3B0069] flex items-center justify-center">
                        <FaPlus size={12} className="text-white" />
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        openCreateSidebar("po");
                      }}
                      className="w-full flex items-center justify-between px-3 py-3 rounded-lg bg-[#F5EEF9] text-[#3B0069] border border-[#E7D9F0] hover:bg-[#EFE4F5]"
                    >
                      <span className="text-sm font-medium">Purchase Order (PO)</span>
                      <span className="w-6 h-6 rounded-full bg-[#3B0069] flex items-center justify-center">
                        <FaPlus size={12} className="text-white" />
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
            {activeTab !== "Home" && (
              <div className="flex items-start justify-between gap-3 mb-3 min-w-0">
                {activeTab === "Material Request" && (
                  <Tabs
                    value={activeRequestStatus}
                    onValueChange={setActiveRequestStatus}
                    className="min-w-0 flex-1"
                  >
                    <TabsList className="min-h-9 h-auto p-1 ml-1 gap-2 !justify-start flex-wrap items-start">
                      {MATERIAL_REQUEST_TABS.map((status, idx) => {
                        const Icon = status.icon;
                        return (
                          <TabsTrigger
                            key={idx}
                            value={status.value}
                            className={`h-7 text-sm gap-1.5 border-none ${activeRequestStatus === status.value
                                ? "bg-sm-primary text-white"
                                : ""
                              }`}
                          >
                            <Icon className="h-3.5 w-3.5" />
                            {status.label} (
                            {
                              requests.filter((r) =>
                                status.value.includes(r.status),
                              ).length
                            }
                            )
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                  </Tabs>
                )}
                {activeTab === "Request for Quotation" && (
                  <Tabs
                    value={activeRfqStatus}
                    onValueChange={setActiveRfqStatus}
                    className="min-w-0 flex-1"
                  >
                    <TabsList className="min-h-9 h-auto p-1 ml-1 gap-2 !justify-start flex-wrap items-start">
                      {RFQ_STATUS_TABS.map((status, idx) => {
                        const Icon = status.icon;
                        return (
                          <TabsTrigger
                            key={idx}
                            value={status.value}
                            className={`h-7 text-sm gap-1.5 border-none ${activeRfqStatus === status.value
                                ? "bg-sm-primary text-white"
                                : ""
                              }`}
                          >
                            <Icon className="h-3.5 w-3.5" />
                            {status.label} (
                            {rfqs.filter((r) => status.value === r.status).length}
                            )
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                  </Tabs>
                )}
                {activeTab === "Purchase Order" && (
                  <Tabs
                    value={activePoStatus}
                    onValueChange={setActivePoStatus}
                    className="min-w-0 flex-1"
                  >
                    <TabsList className="min-h-9 h-auto p-1 ml-1 gap-2 !justify-start flex-wrap items-start">
                      {PO_STATUS_TABS.map((status, idx) => {
                        const Icon = status.icon;
                        return (
                          <TabsTrigger
                            key={idx}
                            value={status.value}
                            className={`h-7 text-sm gap-1.5 border-none ${activePoStatus === status.value
                                ? "bg-sm-primary text-white"
                                : ""
                              }`}
                          >
                            <Icon className="h-3.5 w-3.5" />
                            {status.label} (
                            {
                              purchaseOrders.filter((p) =>
                                status.value.includes(
                                  (p.status || "").toLowerCase(),
                                ),
                              ).length
                            }
                            )
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                  </Tabs>
                )}
                {activeTab !== "Home" && (
                  <div className="flex items-center justify-end gap-3 w-auto lg:w-[35%] shrink-0">
                    {/* <Input
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                icon={<FaSearch className="text-gray-400" />}
              /> */}
                    <button
                      className="flex flex-row items-center gap-1 text-sm text-[#3B0069] border border-gray-200 rounded-md px-2 py-2 bg-[#ECE6F0] hover:bg-[#e0d4e9] transition"
                      onClick={() => {
                        if (activeTab === "Material Request") {
                          setMrSortField((prev) =>
                            prev === "created_on" ? "expectedBy" : "created_on",
                          );
                          return;
                        }
                        setSortRecentDesc((p) => !p);
                      }}
                    >
                      {activeTab === "Material Request"
                        ? MR_SORT_OPTIONS.find(
                          (option) => option.value === mrSortField,
                        )?.label || "Recent"
                        : "Sort by Recent"}
                      <span>
                        <ArrowDown
                          size={14}
                          className={`${activeTab === "Material Request"
                            ? ""
                            : sortRecentDesc
                              ? ""
                              : "rotate-180"
                            } transition-transform`}
                        />
                      </span>
                    </button>

                    {/* {activeTab === "Purchase Order" && (
                <Button
                  className="flex items-center gap-2 bg-gradient-to-r from-[#530092] to-[#9747FF] text-white px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-white/20"
                  onClick={() => setShowOCRModal(true)}
                >
                  <span className="flex items-center gap-1">
                    <Camera size={16} className="text-white" />
                    <Sparkles size={14} className="text-yellow-300 animate-pulse" />
                  </span>
                  <span className="font-medium">Create PO from bill</span>
                </Button>
              )} */}
                  </div>
                )}
              </div>
            )}

            {/* <Card> */}
            <div className="">
              {activeTab === "Home" && (
                <ProcurementHomeDashboard
                  organisationId={organisationId}
                  showHeader={false}
                  showCreateButton={false}
                  onCreateRequest={() => {}}
                  showCreatePanel={false}
                  onCloseCreatePanel={() => {}}
                  onCreateMR={() => {
                    openCreateSidebar("mr");
                  }}
                  onCreateRFQ={() => {
                    openCreateSidebar("rfq");
                  }}
                  onCreatePO={() => {
                    openCreateSidebar("po");
                  }}
                  onViewAlert={handleDashboardAlertView}
                  requests={requests}
                  rfqs={rfqs}
                  purchaseOrders={purchaseOrders}
                />
              )}
              {activeTab === "Material Request" && (
                <>
                  <div className="h-[70vh] overflow-y-auto pr-1">
                    {filtered.length > 0 ? (
                      filtered.map((req) => (
                        <RequestRow
                          key={req.code}
                          request={req}
                          onView={() => {
                            setOpenRequest(req);
                            console.log(req);
                          }}
                        />
                      ))
                    ) : (
                      <div className="flex flex-col items-center justify-center py-14">
                        <img
                          src={NoItemIcon}
                          draggable={false}
                          alt="No Items"
                          className="mb-4"
                          width={220}
                        />
                        <span className="text-gray-500 text-xl">
                          No Items Found
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
              {activeTab === "Request for Quotation" && (
                <div className="h-[72vh] overflow-y-auto pr-1">
                  {filteredRfqs.length > 0 ? (
                    filteredRfqs.map((r) => (
                      <RfqRow
                        key={r.id}
                        rfq={r}
                        onView={(rfq) => setOpenRfq(rfq)}
                      />
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-14">
                      <img
                        src={NoItemIcon}
                        draggable={false}
                        alt="No Items"
                        className="mb-4"
                        width={220}
                      />
                      <span className="text-gray-500 text-xl">
                        No Items Found
                      </span>
                    </div>
                  )}
                </div>
              )}
              {activeTab === "Purchase Order" && (
                <div className="h-[72vh] overflow-y-auto pr-1">
                  {filteredPos.length > 0 ? (
                    filteredPos.map((po, idx) => (
                      <PurchaseOrderRow
                        key={`${po.id}_${idx}`}
                        po={po}
                        onView={(p) => setOpenPo(p)}
                      />
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-14">
                      <img
                        src={NoItemIcon}
                        draggable={false}
                        alt="No Items"
                        className="mb-4"
                        width={220}
                      />
                      <span className="text-gray-500 text-xl">
                        No Items Found
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* </Card> */}
          </>
        ) : null}
        {openRfq && (
          <QuotationSummary
            rfq={openRfq}
            onClose={() => {
              setOpenRfq(null);
              getRfqs();
              getPos();
            }}
            onProceedToPO={() => {
              setActiveTab("Purchase Order");
            }}
          />
        )}
        {openPo && (
          <PurchaseOrder
            rfqId={openPo.rfq_number}
            poCode={openPo.po_number}
            po={openPo}
            vendor={openPo.vendor_name}
            onApproved={() => {
              setActiveSection("Request & Order");
              setActiveTab("Purchase Order");
              setActivePoStatus(PO_STATUS_TABS[1].value);
              getPos();
            }}
            onClose={() => {
              setOpenPo(null);
              getPos();
            }}
          />
        )}
        
        {isCreateRequestSidebarOpen && (
          <div className="fixed inset-0 z-40 bg-[#3B0069]/10 backdrop-blur-[3px] transition-all duration-300" />
        )}
        {showCreateMR && (
          <div
            ref={requestSidebarRef}
            className="fixed inset-y-0 right-0 z-50 w-full sm:max-w-[40rem] h-full bg-white shadow-2xl rounded-l-2xl overflow-y-auto"
          >
            <CreateMaterialRequest
              asSidebar
              onCreateMR={() => {
                setShowCreateMR(false);
                setActiveSection("Request & Order");
                setActiveTab("Material Request");
                setActiveRequestStatus(inProgressRequestStatus);
                getMaterialRequests();
              }}
              onClose={() => setShowCreateMR(false)}
            />
          </div>
        )}
        {showCreateRFQ && (
          <div
            ref={requestSidebarRef}
            className="fixed inset-y-0 right-0 z-50 w-full sm:max-w-[40rem] h-full bg-white shadow-2xl rounded-l-2xl overflow-y-auto"
          >
            <RequestForQuoatation
              onCreateRFQ={() => {
                setShowCreateRFQ(false);
                setActiveSection("Request & Order");
                setActiveTab("Request for Quotation");
                setActiveRfqStatus("OPEN");
                setOpenRfq(null);
              }}
              onClose={() => {
                setShowCreateRFQ(false);
                getRfqs();
                getPos();
              }}
            />
          </div>
        )}
        {showCreatePO && (
          <div
            ref={requestSidebarRef}
            className="fixed inset-y-0 right-0 z-50 w-full sm:max-w-[40rem] h-full bg-white shadow-2xl rounded-l-2xl overflow-y-auto"
          >
            <Request
              onSuccess={() => {
                setShowCreatePO(false);
                setActiveSection("Request & Order");
                setActiveTab("Purchase Order");
                setActivePoStatus(PO_STATUS_TABS[1].value);
                getPos();
              }}
              onClose={() => setShowCreatePO(false)}
            />
          </div>
        )}
        {showCreateWarehouseMaterial && (
          <CreateWarehouseMaterial
            onClose={() => {
              setShowCreateWarehouseMaterial(false);
              getMaterials();
            }}
            onRedirectToMaterialLibrary={() => {
              setShowCreateWarehouseMaterial(false);
              navigate("/dashboard/settings?section=material-library");
            }}
            onSuccess={(newMaterial) => {
              // Optionally refresh your materials list here
              console.log("New warehouse material created:", newMaterial);
              getMaterials();
              // You might want to call a function to refresh the warehouse materials list
            }}
          />
        )}
        {selectedMaterial && (
          <WarehouseMaterialSidebar
            material={selectedMaterial}
            onClose={() => setSelectedMaterial(null)}
          />
        )}
        {showSendReceiveSideBar && (
          <SendReceiveSideBar
            material={showSendReceiveSideBar}
            onClose={() => {
              setShowSendReceiveSideBar(false);
              getMaterials();
            }}
          />
        )}
        {openRequest && (
          <MaterialRequest
            Request={openRequest}
            onClose={() => {
              setOpenRequest(null);
              getMaterialRequests();
              getRfqs();
              getPos();
            }}
            onApprove={() => {
              setActiveTab("Material Request");
              setActiveRequestStatus(
                MATERIAL_REQUEST_TABS.find((tab) => tab.label === "In Progress")
                  ?.value || MATERIAL_REQUEST_TABS[1].value,
              );
            }}
            onReject={() => {
              setActiveTab("Material Request");
              setActiveRequestStatus(
                MATERIAL_REQUEST_TABS.find((tab) => tab.label === "Rejected")
                  ?.value ||
                MATERIAL_REQUEST_TABS[MATERIAL_REQUEST_TABS.length - 1].value,
              );
            }}
            onProceedToRFQ={() => {
              setActiveTab("Request for Quotation");
              setActiveRfqStatus("OPEN");
            }}
          />
        )}
        {showOCRModal && (
          <OCRtoPO
            isOpen={showOCRModal}
            onClose={() => setShowOCRModal(false)}
            organisationId={organisationId}
          />
        )}
      </LoadingScreen>
    </div>
  );
}
